import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import ROUTES from '../assets/routes'
import { ViewType } from '../assets/constants'
import { Platform } from '../proto/generated/user-preference'
import { Security } from '../proto/generated/security'
import { setFinancialPanelOpen, setSeasonalsPanelOpen, setTechnicalsPanelOpen } from '../reducer/home.reducer'
import { updatePrimarySecurityAsync } from '../reducer/home.thunks'
import { selectActiveLayoutId } from '../selectors/home'
import { selectIsMobile } from '../selectors/device'
import { selectDefaultLayoutId } from '../selectors/user'
import { AppDispatch } from '../store'
import { FINANCIAL_SHAREHOLDING_SECTION_ID } from '../util/financialData'
import { getSecurityKey, isFnOSecurity } from '../util/security'
import { getViewFromPathname } from './useSelectView'

const PANEL_ACTIONS = {
    seasonals: () => setSeasonalsPanelOpen(true),
    technicals: () => setTechnicalsPanelOpen(true),
    shareholding: () => setFinancialPanelOpen({ open: true, section: FINANCIAL_SHAREHOLDING_SECTION_ID })
} as const

export type OverviewPanelId = keyof typeof PANEL_ACTIONS

/** Opens an overview side panel; off-chart, syncs primary security and navigates to chart first. */
const useOverviewPanelNavigation = (panel: OverviewPanelId) => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const isMobile = useSelector(selectIsMobile)
    const defaultLayoutId = useSelector(selectDefaultLayoutId(isMobile ? Platform.MOBILE : Platform.DESKTOP))

    return useCallback(
        async (security: Security | undefined) => {
            if (!security?.code) return

            const routeView = getViewFromPathname(window.location.pathname)
            const isOnChart = routeView === ViewType.CHART || routeView === ViewType.SYMBOL

            if (isOnChart) {
                dispatch(PANEL_ACTIONS[panel]())
                return
            }

            const securityKey = getSecurityKey(security.exchange, security.code)
            const isFnO = isFnOSecurity(security)
            await updatePrimarySecurityAsync({
                key: securityKey,
                expressionMode: false,
                securities: [securityKey],
                underlyingSecurityCode: getSecurityKey(
                    security.exchange,
                    isFnO ? security.underlyingSecurityCode! : security.code
                )
            })(dispatch)

            dispatch(PANEL_ACTIONS[panel]())
            navigate(generatePath(ROUTES.chart, { layoutId: activeLayoutId || defaultLayoutId || '' }))
        },
        [activeLayoutId, defaultLayoutId, dispatch, navigate, panel]
    )
}

export default useOverviewPanelNavigation
